import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = '/version.json';
const RELOADED_VERSION_STORAGE_KEY = 'rid-app-update-reloaded-version';

type AppVersionInfo = {
  buildId: string | null;
  gitSha: string | null;
  imageTag: string | null;
  deployedAt: string | null;
};

const normalizeVersionValue = (value: unknown): string | null => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const createVersionInfo = (input?: Partial<Record<keyof AppVersionInfo, unknown>> | null): AppVersionInfo | null => {
  const version: AppVersionInfo = {
    buildId: normalizeVersionValue(input?.buildId),
    gitSha: normalizeVersionValue(input?.gitSha),
    imageTag: normalizeVersionValue(input?.imageTag),
    deployedAt: normalizeVersionValue(input?.deployedAt),
  };

  if (!version.buildId && !version.gitSha && !version.imageTag && !version.deployedAt) {
    return null;
  }

  return version;
};

const getStableVersionIdentifier = (version: AppVersionInfo | null): string | null => {
  if (!version) return null;

  return version.buildId || version.gitSha || version.imageTag || null;
};

const getVersionSignature = (version: AppVersionInfo | null): string | null => {
  const stableVersionIdentifier = getStableVersionIdentifier(version);
  if (!stableVersionIdentifier) return null;

  return JSON.stringify({
    stableVersionIdentifier,
  });
};

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storage = window.localStorage;
    if (!storage) return null;
    if (typeof storage.getItem !== 'function') return null;
    if (typeof storage.setItem !== 'function') return null;
    return storage;
  } catch {
    // Browser policy can deny access to the storage property itself.
    return null;
  }
};

const readStoredVersionSignature = (): string | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    return normalizeVersionValue(storage.getItem(RELOADED_VERSION_STORAGE_KEY));
  } catch {
    return null;
  }
};

const writeStoredVersionSignature = (value: string): void => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(RELOADED_VERSION_STORAGE_KEY, value);
  } catch {
    // Ignore storage write failures and still allow reload.
  }
};

const getCurrentVersionInfo = (): AppVersionInfo | null => {
  if (typeof window === 'undefined') return null;

  return createVersionInfo({
    buildId: window.__APP_CONFIG__?.BUILD_ID,
    gitSha: window.__APP_CONFIG__?.GIT_SHA,
    imageTag: window.__APP_CONFIG__?.IMAGE_TAG,
    deployedAt: window.__APP_CONFIG__?.DEPLOYED_AT,
  });
};

const fetchLatestVersionInfo = async (): Promise<AppVersionInfo | null> => {
  const response = await fetch(`${VERSION_URL}?ridUpdateCheck=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Cache-Control': 'no-cache',
      'Accept': 'application/json',
    },
  });

  if (!response.ok) return null;

  const payload = await response.json().catch(() => null);
  if (!payload || typeof payload !== 'object') return null;

  return createVersionInfo({
    buildId: (payload as Record<string, unknown>).buildId,
    gitSha: (payload as Record<string, unknown>).gitSha,
    imageTag: (payload as Record<string, unknown>).imageTag,
    deployedAt: (payload as Record<string, unknown>).deployedAt,
  });
};

export default function useAppUpdateCheck() {
  const [currentVersionSignature, setCurrentVersionSignature] = useState<string | null>(null);
  const [latestVersionSignature, setLatestVersionSignature] = useState<string | null>(null);
  const [dismissedVersionSignature, setDismissedVersionSignature] = useState<string | null>(null);
  const [reloadedVersionSignature, setReloadedVersionSignature] = useState<string | null>(
    () => readStoredVersionSignature(),
  );
  const currentVersionRef = useRef<AppVersionInfo | null>(null);
  const checkInFlightRef = useRef(false);

  const checkForUpdate = useCallback(async () => {
    if (typeof window === 'undefined' || import.meta.env.DEV) return;
    if (checkInFlightRef.current) return;

    checkInFlightRef.current = true;

    try {
      let currentVersion = currentVersionRef.current;
      if (!currentVersion) {
        currentVersion = getCurrentVersionInfo();
        if (currentVersion) {
          currentVersionRef.current = currentVersion;
          setCurrentVersionSignature(getVersionSignature(currentVersion));
        }
      }

      const latestVersion = await fetchLatestVersionInfo();
      if (!latestVersion) return;

      const latestSignature = getVersionSignature(latestVersion);
      setLatestVersionSignature(latestSignature);

      if (!currentVersion) {
        currentVersionRef.current = latestVersion;
        setCurrentVersionSignature(latestSignature);
        return;
      }

      const currentSignature = getVersionSignature(currentVersion);
      if (latestSignature === currentSignature) {
        setDismissedVersionSignature(null);
      }
    } catch {
      // Ignore transient update-check failures.
    } finally {
      checkInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || import.meta.env.DEV) return undefined;

    const currentVersion = getCurrentVersionInfo();
    currentVersionRef.current = currentVersion;
    setCurrentVersionSignature(getVersionSignature(currentVersion));

    void checkForUpdate();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    }, UPDATE_CHECK_INTERVAL_MS);

    const handleFocus = () => {
      void checkForUpdate();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  const isUpdateAvailable = useMemo(() => {
    if (!currentVersionSignature || !latestVersionSignature) return false;
    if (currentVersionSignature === latestVersionSignature) return false;
    if (reloadedVersionSignature === latestVersionSignature) return false;
    return dismissedVersionSignature !== latestVersionSignature;
  }, [
    currentVersionSignature,
    dismissedVersionSignature,
    latestVersionSignature,
    reloadedVersionSignature,
  ]);

  const dismiss = useCallback(() => {
    if (!latestVersionSignature) return;
    setDismissedVersionSignature(latestVersionSignature);
  }, [latestVersionSignature]);

  const reload = useCallback(() => {
    if (latestVersionSignature && typeof window !== 'undefined') {
      writeStoredVersionSignature(latestVersionSignature);
      setReloadedVersionSignature(latestVersionSignature);
    }
    window.location.reload();
  }, [latestVersionSignature]);

  return {
    currentVersionSignature,
    latestVersionSignature,
    isUpdateAvailable,
    dismiss,
    reload,
  };
}
